import { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { VisuallyHidden } from 'radix-ui'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Zap } from 'lucide-react'
import { toast } from 'sonner'
import { useOrgPath } from '@/lib/pathFor'
import { formatCurrency } from '@/lib/formatters'
import { shortContainerType, CONTAINER_TYPES } from '@/lib/constants'
import { rateSourceLabel, rateSourceBadgeClass } from '@/lib/rateSource'

interface CarrierOption {
  id: string
  name: string
  code: string | null
}

interface FetchedRate {
  id: string
  rate_source: string | null
  source_label?: string | null
  carrier_offer_id: string | null
  carrier_price_request_id: string | null
  carrier_quotation_number: string | null
  carrier_id: string | null
  carrier_code: string | null
  carrier_name: string | null
  product_type: string | null
  transit_days: number | null
  departure_date: string | null
  arrival_date: string | null
  total_per_container_usd: number | null
  total_all_containers_usd: number | null
  offer_valid_until: string | null
}

interface UnparseableNote {
  carrier: string
  carrier_name: string
  count: number
  reason: string
}

interface BookingData {
  id: string
  carrier_booking_id: string | null
  carrier_id: string | null
  sailing_date: string | null
  eta: string | null
  buy_rate: number | null
  markup: number | null
  buy_shipment: number | null
  container_type: string | null
  container_count: number | null
  quote_snapshot?: {
    offer_valid_until: string | null
  } | null
}

interface BookingDrawerProps {
  shipmentId: string
  originPort: string | null
  destinationPort: string | null
  carriers: CarrierOption[]
  carrierCodesWithApi: string[]
  carrierCodesWithRateSheets: string[]
  booking?: BookingData | null // when provided, drawer is in edit mode; container fields come from here
  open: boolean
  onClose: () => void
}

export default function BookingDrawer({
  shipmentId, originPort, destinationPort,
  carriers, carrierCodesWithApi, carrierCodesWithRateSheets, booking, open, onClose
}: BookingDrawerProps) {
  const pathFor = useOrgPath()
  const isEditing = !!booking

  const [carrierId, setCarrierId] = useState('')
  const [carrierBookingId, setCarrierBookingId] = useState('')
  const [sailingDate, setSailingDate] = useState('')
  const [eta, setEta] = useState('')
  const [buyRate, setBuyRate] = useState('')
  const [markup, setMarkup] = useState('')
  const [shipmentRate, setShipmentRate] = useState('')
  const [quoteValidUntil, setQuoteValidUntil] = useState('')
  const [selectedRateId, setSelectedRateId] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  
  // Container fields - editable by operator
  const [containerType, setContainerType] = useState('')
  const [containerCount, setContainerCount] = useState<string>('1')

  // Fetched rates state
  const [fetchedRates, setFetchedRates] = useState<FetchedRate[]>([])
  const [unparseableNotes, setUnparseableNotes] = useState<UnparseableNote[]>([])
  const [fetching, setFetching] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)

  // Reset / pre-fill form when opened
  useEffect(() => {
    if (open) {
      if (booking) {
        setCarrierId(booking.carrier_id || '')
        setCarrierBookingId(booking.carrier_booking_id || '')
        setSailingDate(booking.sailing_date?.slice(0, 10) || '')
        setEta(booking.eta?.slice(0, 10) || '')
        setBuyRate(booking.buy_rate != null ? String(booking.buy_rate) : '')
        setMarkup(booking.markup != null ? String(booking.markup) : '')
        setShipmentRate(booking.buy_shipment != null ? String(booking.buy_shipment) : '')
        setQuoteValidUntil(booking.quote_snapshot?.offer_valid_until?.slice(0, 10) || defaultQuoteValidUntil())
        setContainerType(booking.container_type || '')
        setContainerCount(booking.container_count != null ? String(booking.container_count) : '1')
      } else {
        setCarrierId('')
        setCarrierBookingId('')
        setSailingDate('')
        setEta('')
        setBuyRate('')
        setMarkup('')
        setShipmentRate('')
        setQuoteValidUntil(defaultQuoteValidUntil())
        setContainerType('')
        setContainerCount('1')
      }
      setSelectedRateId(null)
      setFetchedRates([])
      setHasFetched(false)
    }
  }, [open, booking])

  // Route context label - use entered values, not just booking values
  const countNum = parseInt(containerCount) || 1
  const routeLabel = [
    [originPort, destinationPort].filter(Boolean).join(' \u2192 '),
    containerCount && containerType ? `${countNum} \u00d7 ${shortContainerType(containerType)}` : null
  ].filter(Boolean).join(' \u00b7 ')

  // Computed pricing
  const buyRateCents = buyRate ? Math.round(parseFloat(buyRate) * 100) : 0
  const markupCents = markup ? Math.round(parseFloat(markup) * 100) : 0
  const shipmentRateCents = shipmentRate ? Math.round(parseFloat(shipmentRate) * 100) : 0
  const sellRateCents = buyRateCents + markupCents
  const totalCents = sellRateCents * countNum + shipmentRateCents

  const sellRateDisplay = sellRateCents > 0 ? formatCurrency(sellRateCents / 100) : null
  const totalDisplay = totalCents > 0 ? formatCurrency(totalCents / 100) : null
  const hasPricing = buyRateCents > 0

  function clearFetchedRates() {
    setSelectedRateId(null)
    setFetchedRates([])
    setUnparseableNotes([])
    setHasFetched(false)
  }

  // Fetch live rates
  async function handleFetchRates() {
    if (!carrierId) {
      toast.error('Select a carrier first')
      return
    }
    if (!sailingDate) {
      toast.error('Sailing date is required to fetch rates')
      return
    }
    if (!containerType) {
      toast.error('Container type is required to fetch rates')
      return
    }
    setFetching(true)
    try {
      const selectedCarrier = carriers.find(c => c.id === carrierId)
      const ratesPath = isEditing ? `/bookings/${booking!.id}/live_rates` : `/shipments/${shipmentId}/live_rates`
      const response = await fetch(pathFor(ratesPath), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({
          origin_locode: originPort || '',
          destination_locode: destinationPort || '',
          container_type: containerType || '',
          container_count: String(countNum),
          departure_date: sailingDate || '',
          carrier_code: selectedCarrier?.code || '',
        }),
      })
      if (response.ok) {
        const data = await response.json()
        const rates = data.offers || []
        const unparseable = data.unparseable || []
        const errors: { carrier: string; error: string }[] = data.errors || []
        setFetchedRates(rates)
        setUnparseableNotes(unparseable)
        setHasFetched(true)
        if (rates.length === 0 && errors.length > 0) {
          // Only surface carrier errors when nothing came back — a contract rate
          // can carry the result even if the carrier API erred or has no creds.
          errors.forEach(e => toast.error(e.error))
        } else if (rates.length === 0 && unparseable.length === 0) {
          toast.info('No rates found for this carrier and route')
        }
      } else {
        toast.error('Failed to fetch rates')
      }
    } catch {
      toast.error('Failed to fetch rates')
    } finally {
      setFetching(false)
    }
  }

  // Select a fetched rate — pre-fill pricing
  function handleSelectRate(rate: FetchedRate) {
    setSelectedRateId(rate.id)
    if (rate.total_per_container_usd != null) {
      setBuyRate(String(rate.total_per_container_usd))
    }
    if (rate.departure_date) {
      setSailingDate(rate.departure_date.slice(0, 10))
    }
    if (rate.arrival_date) {
      setEta(rate.arrival_date.slice(0, 10))
    }
    if (rate.offer_valid_until) {
      setQuoteValidUntil(rate.offer_valid_until.slice(0, 10))
    }
  }

  // Submit booking (create or update)
  function handleSubmit() {
    if (!carrierId) {
      toast.error('Carrier is required')
      return
    }
    if (!quoteValidUntil) {
      toast.error('Valid until date is required')
      return
    }
    setSubmitting(true)

    const bookingData: Record<string, string | undefined> = {
      carrier_id: carrierId,
      sailing_date: sailingDate || undefined,
      eta: eta || undefined,
      quote_valid_until: quoteValidUntil || undefined,
    }

    if (!isEditing) {
      bookingData.shipment_id = shipmentId
      bookingData.status = carrierBookingId ? 'confirmed' : 'quoted'
    }

    if (isEditing || carrierBookingId.trim()) {
      bookingData.carrier_booking_id = carrierBookingId
    }

    if (buyRate) bookingData.buy_rate_dollars = buyRate
    if (markup) bookingData.markup_dollars = markup
    if (shipmentRate) bookingData.buy_shipment_dollars = shipmentRate
    
    // Include container fields in submission
    if (containerType) bookingData.container_type = containerType
    if (containerCount) bookingData.container_count = containerCount

    // Link to carrier offer if from fetched rates
    if (selectedRateId) {
      bookingData.carrier_offer_id = selectedRateId
    }

    if (isEditing) {
      router.patch(pathFor(`/bookings/${booking!.id}`), { booking: bookingData }, {
        preserveState: false,
        onSuccess: () => {
          onClose()
        },
        onError: () => {
          toast.error('Failed to update booking')
          setSubmitting(false)
        },
      })
    } else {
      router.post(pathFor('/bookings'), { booking: bookingData }, {
        preserveState: false,
        onSuccess: () => {
          onClose()
        },
        onError: () => {
          toast.error('Failed to create booking')
          setSubmitting(false)
        },
      })
    }
  }

  const title = isEditing ? 'Edit Booking' : 'Add Booking'

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <SheetContent side="right" className="w-[480px] p-0 flex flex-col [&>button]:hidden" aria-describedby={undefined}>
        <VisuallyHidden.Root>
          <SheetTitle>{title}</SheetTitle>
        </VisuallyHidden.Root>
        {/* Header */}
        <div className="px-5 py-4 border-b flex items-start justify-between">
          <div>
            <h3 className="text-[15px] font-bold text-foreground">{title}</h3>
            {routeLabel && (
              <p className="text-[12px] font-medium text-muted-foreground mt-0.5">{routeLabel}</p>
            )}
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground cursor-pointer mt-0.5">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Carrier */}
          <div className="space-y-1.5">
            <label className="text-[12px] font-semibold text-muted-foreground">Carrier *</label>
            <select
              value={carrierId}
              onChange={(e) => {
                setCarrierId(e.target.value)
                clearFetchedRates()
              }}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-[13px] cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="">Select carrier...</option>
              {carriers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-[12px] font-semibold text-muted-foreground">Carrier Booking ID</label>
            <Input
              value={carrierBookingId}
              onChange={e => setCarrierBookingId(e.target.value)}
              className="h-9 text-[13px]"
              placeholder="Optional carrier confirmation ID"
            />
            <p className="text-[10px] text-muted-foreground italic">
              {isEditing ? 'Carrier confirmation reference used for SI, BL, and shipment matching.' : 'Optional — enter if booking was made outside the system'}
            </p>
          </div>

          {/* Schedule */}
          <div className="space-y-2">
            <p className="text-[10px] font-semibold text-muted-foreground tracking-wider uppercase">Schedule</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[12px] font-semibold text-muted-foreground">Sailing Date *</label>
                <Input
                  type="date"
                  value={sailingDate}
                  onChange={e => {
                    setSailingDate(e.target.value)
                    clearFetchedRates()
                  }}
                  className="h-9 text-[13px] cursor-pointer"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[12px] font-semibold text-muted-foreground">ETA</label>
                <Input type="date" value={eta} onChange={e => setEta(e.target.value)} className="h-9 text-[13px] cursor-pointer" />
              </div>
            </div>
          </div>

          {/* Containers */}
          <div className="space-y-2">
            <p className="text-[10px] font-semibold text-muted-foreground tracking-wider uppercase">Containers</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[12px] font-semibold text-muted-foreground">Type *</label>
                <select
                  value={containerType}
                  onChange={(e) => {
                    setContainerType(e.target.value)
                    clearFetchedRates()
                  }}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-[13px] cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  {CONTAINER_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-[12px] font-semibold text-muted-foreground">Count *</label>
                <Input
                  type="number"
                  min="1"
                  value={containerCount}
                  onChange={e => {
                    setContainerCount(e.target.value)
                    clearFetchedRates()
                  }}
                  className="h-9 text-[13px]"
                  placeholder="1"
                />
              </div>
            </div>
          </div>

          {/* Fetch Rates — only enabled when selected carrier has API credentials */}
          {(() => {
            const selectedCarrier = carriers.find(c => c.id === carrierId)
            // A carrier can be fetched if it has any rate source — a live API
            // credential or contract rate sheets.
            const carrierHasRates = selectedCarrier?.code
              ? carrierCodesWithApi.includes(selectedCarrier.code) || carrierCodesWithRateSheets.includes(selectedCarrier.code)
              : false
            return (
              <button
                onClick={handleFetchRates}
                disabled={fetching || !carrierId || !carrierHasRates}
                className="w-full flex items-center justify-center gap-2 rounded-md border py-2 text-[13px] text-muted-foreground hover:text-foreground font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Zap className="h-3.5 w-3.5" />
                {fetching ? 'Fetching...' : 'Fetch Rates from Carrier'}
              </button>
            )
          })()}

          {/* Fetched Rates */}
          {hasFetched && (fetchedRates.length > 0 || unparseableNotes.length > 0) && (
            <div className="space-y-2">
              {fetchedRates.length > 0 && (
                <p className="text-[11px] text-muted-foreground">{fetchedRates.length} rate{fetchedRates.length !== 1 ? 's' : ''} found</p>
              )}
              {fetchedRates.map(rate => {
                const isSelected = selectedRateId === rate.id
                return (
                  <button
                    key={rate.id}
                    onClick={() => handleSelectRate(rate)}
                    className={`w-full text-left rounded-md border p-3 cursor-pointer transition-colors ${
                      isSelected
                        ? 'border-blue-300 bg-blue-50/60'
                        : 'border-border hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <p className="text-[13px] font-semibold text-foreground">{rate.product_type?.replace(/_/g, ' ') || 'Standard'}</p>
                          {rateSourceLabel(rate.rate_source) && (
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${rateSourceBadgeClass(rate.rate_source)}`}>
                              {rateSourceLabel(rate.rate_source)}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          {[
                            rate.transit_days ? `${rate.transit_days} days transit` : null,
                            rate.departure_date ? `Departs ${rate.departure_date.slice(0, 10)}` : null,
                          ].filter(Boolean).join(' \u00b7 ')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[13px] font-bold text-foreground">{formatCurrency(rate.total_per_container_usd)}/ctr</p>
                        {isSelected && <p className="text-[10px] text-blue-600 font-medium">Selected</p>}
                      </div>
                    </div>
                  </button>
                )
              })}
              {/*
                Unparseable offers are shown as an informational note — the
                operator can still check the carrier's own site for these,
                but we cannot safely display a total for them here.
              */}
              {unparseableNotes.map(note => (
                <p key={note.carrier} className="text-[11px] text-muted-foreground italic">
                  {note.carrier_name}: {note.count} offer{note.count !== 1 ? 's' : ''} not shown (unsupported pricing structure)
                </p>
              ))}
            </div>
          )}

          {/* Divider — "or enter manually" */}
          <div className="flex items-center gap-3">
            <div className="flex-1 border-t" />
            <span className="text-[11px] text-muted-foreground">or enter manually</span>
            <div className="flex-1 border-t" />
          </div>

          {/* Pricing */}
          <div className="space-y-3">
            <p className="text-[10px] font-semibold text-muted-foreground tracking-wider uppercase">Pricing</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[12px] font-semibold text-muted-foreground">Buy Rate (per ctr) *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-muted-foreground">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={buyRate}
                    onChange={e => setBuyRate(e.target.value)}
                    className="h-9 text-[13px] pl-7"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[12px] font-semibold text-muted-foreground">Markup (per ctr)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-muted-foreground">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={markup}
                    onChange={e => setMarkup(e.target.value)}
                    className="h-9 text-[13px] pl-7"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            {/* Shipment Rate */}
            <div className="space-y-1">
              <label className="text-[12px] font-semibold text-muted-foreground">Shipment Rate</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-muted-foreground">$</span>
                <Input
                  type="number"
                  step="0.01"
                  value={shipmentRate}
                  onChange={e => setShipmentRate(e.target.value)}
                  className="h-9 text-[13px] pl-7"
                  placeholder="0.00"
                />
              </div>
              <p className="text-[10px] text-muted-foreground italic">Passed through to client at cost</p>
            </div>
          </div>

          {/* Summary */}
          {hasPricing && (
            <div className="rounded-lg border bg-muted/30 px-4 py-3 space-y-2">
              <div className="flex justify-between text-[12px]">
                <span className="text-muted-foreground">Sell rate</span>
                <span className="font-medium text-foreground">{sellRateDisplay}/ctr</span>
              </div>
              <div className="border-t" />
              <div className="flex justify-between text-[13px] font-semibold">
                <span className="text-foreground">Total ({countNum} ctr{countNum !== 1 ? 's' : ''}{shipmentRateCents > 0 ? ' + shipment' : ''})</span>
                <span className="text-foreground">{totalDisplay}</span>
              </div>
            </div>
          )}

          {/* Offer validity */}
          <div className="space-y-2">
            <p className="text-[10px] font-semibold text-muted-foreground tracking-wider uppercase">Offer</p>
            <div className="space-y-1">
              <label className="text-[12px] font-semibold text-muted-foreground">Valid Until *</label>
              <Input
                type="date"
                value={quoteValidUntil}
                onChange={e => setQuoteValidUntil(e.target.value)}
                className="h-9 text-[13px] cursor-pointer"
              />
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !carrierId}>
            {submitting
              ? (isEditing ? 'Saving...' : 'Creating...')
              : (isEditing ? 'Save Changes' : 'Add Booking')
            }
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}

function defaultQuoteValidUntil() {
  const date = new Date()
  date.setDate(date.getDate() + 14)
  return date.toISOString().slice(0, 10)
}
